import {
  Container,
  Grid,
  Paper,
  Text,
  Title,
  Card,
  Group,
  Table,
  Button,
  Badge,
} from "@mantine/core";
import {
  IconFileText,
  IconClock,
  IconCircleCheck,
  IconAlertTriangle,
  IconPlus,
} from "@tabler/icons-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useTenderDashboard, useExpiringTenders } from "../hooks/useTenders";
import dayjs from "dayjs";

export function TenderDashboard() {
  const navigate = useNavigate();
  const { data: dashboard, isLoading: dashboardLoading } = useTenderDashboard();
  const { data: expiringTenders, isLoading: expiringLoading } =
    useExpiringTenders();

  const chartData = [
    { name: "Published", value: dashboard?.publishedTenders || 0 },
    { name: "Active", value: dashboard?.activeTenders || 0 },
    { name: "Awarded", value: dashboard?.awardedTenders || 0 },
    { name: "Expiring", value: dashboard?.expiringTenders || 0 },
  ];

  const stats = [
    {
      title: "Total Tenders",
      value: dashboard?.totalTenders || 0,
      icon: IconFileText,
      color: "blue",
    },
    {
      title: "Active Tenders",
      value: dashboard?.activeTenders || 0,
      icon: IconClock,
      color: "yellow",
    },
    {
      title: "Awarded Tenders",
      value: dashboard?.awardedTenders || 0,
      icon: IconCircleCheck,
      color: "green",
    },
    {
      title: "Expiring Soon",
      value: dashboard?.expiringTenders || 0,
      icon: IconAlertTriangle,
      color: "red",
    },
  ];

  if (dashboardLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading dashboard...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Tender Dashboard</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate("/tenders/new")}
        >
          New Tender
        </Button>
      </Group>

      {/* Stats Cards */}
      <Grid mb="xl">
        {stats.map((stat) => (
          <Grid.Col key={stat.title} span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg">
              <Group>
                <stat.icon
                  size={32}
                  color={`var(--mantine-color-${stat.color}-6)`}
                />
                <div>
                  <Text size="lg" fw={700}>
                    {stat.value}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {stat.title}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {/* Chart */}
      <Paper shadow="sm" p="xl" mb="xl">
        <Title order={3} mb="md">
          Tender Overview
        </Title>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="var(--mantine-color-blue-6)" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Expiring Tenders */}
      <Paper shadow="sm" p="xl">
        <Title order={3} mb="md">
          Expiring Tenders
        </Title>
        {expiringLoading ? (
          <Text>Loading expiring tenders...</Text>
        ) : expiringTenders && expiringTenders.length > 0 ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Reference</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Deadline</Table.Th>
                <Table.Th>Days Left</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expiringTenders.map((tender) => {
                const daysLeft = dayjs(tender.submissionDeadline).diff(
                  dayjs(),
                  "day"
                );
                return (
                  <Table.Tr key={tender.id}>
                    <Table.Td>
                      <Text fw={500}>{tender.referenceNo}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text lineClamp={2}>{tender.description}</Text>
                    </Table.Td>
                    <Table.Td>
                      {dayjs(tender.submissionDeadline).format("MMM DD, YYYY HH:mm")}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          daysLeft <= 7
                            ? "red"
                            : daysLeft <= 14
                              ? "yellow"
                              : "green"
                        }
                      >
                        {daysLeft} days
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => navigate(`/tenders/${tender.id}`)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed">No tenders expiring soon</Text>
        )}
      </Paper>
    </Container>
  );
}
